<template>
  <div class="form-group" :class="{ 'mb-4': showFileInput}" >
    <label v-if="label" :for="id" class="form-label">{{ label }}</label>
    <input v-if="showFileInput" :id="id" class="form-control" type="file" :disabled="isDisabled"
      @change="onFileSelected" />
    <div v-if="error" class="invalid-feedback d-block">{{ error }}</div>
    <div v-if="modelValue" :class="{ 'mt-4': showFileInput}" class="d-flex gap-2 justify-content-between align-items-center">
      <div class="mb-2"><small>{{ value.fileName }}</small></div>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-secondary" @click="viewFile">View</button>
        <button type="button" class="btn btn-primary" @click="downloadFile">Download</button>
        <button v-if="!isDisabled" type="button" class="btn btn-danger" @click="deleteFile">Delete</button>
      </div>
    </div>
  </div>
  <div v-if="customError" class="mt-4 d-flex gap-2 justify-content-between align-items-center">
    <div class="mb-2"><small>{{ customError }}</small></div>
    <div class="d-flex gap-2">
      <button type="button" class="btn btn-danger" @click="clearError">Clear</button>
    </div>
  </div>
  <DocumentViewer ref="documentViewer" />
</template>

<script>
import axios from 'axios';
import SchemeBusinessService from '../../services/scheme-business-service';
import DocumentViewer from '../DocumentViewer.vue';

export default {
  name: 'SchemeBusinessFileUpload',
  components: {
    DocumentViewer,
  },
  props: {
    modelValue: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      default: 'File Upload',
    },
    acceptTypes: {
      type: Array,
      default: () => ['image/png', 'image/jpeg', 'image/gif', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    },
    description: {
      type: String,
      default: '',
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    validator: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    showFileInput: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      customError: '',
      busy: false,
    };
  },
  computed: {
    value: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    error() {
      return this.validator?.$errors?.[0]?.$message || this.customError;
    },
    isDisabled() {
      return this.disabled || this.busy || this.readonly;
    },
    schemeBusinessId() {
      return this.$route.params.schemeBusinessId;
    },
  },
  methods: {
    onFileSelected(e) {
      this.customError = '';
      const file = e.target?.files?.[0];

      if (this.acceptTypes.indexOf(file.type) === -1) {
        this.customError = `File must be one of the following types: ${this.acceptTypes.join(
          ', '
        )}.`;
        return;
      }

      const config = {
        headers: {
          'x-amz-acl': 'private',
        },
      };

      this.busy = true;
      SchemeBusinessService.getSignedUrl(this.schemeBusinessId, file.name)
        .then((result) => {
          axios.put(result, file, config).then((fileResult) => {

            this.value = { fileUrl: fileResult.config.url.split('?')[0], fileName: file.name };
            this.busy = false;
          });
        })
        .catch(() => {
          this.customError = 'Could not upload file, please try again.';
          this.busy = false;
        });
    },
    downloadFile() {
      SchemeBusinessService.getDownloadUrl(this.value.fileUrl)
        .then((result) => {
          window.open(result, '_blank');
        })
        .catch(() => {
          this.customError = 'Could not download file, please try again.';
        });
    },
    viewFile() {
      SchemeBusinessService.getDownloadUrl(this.value.fileUrl)
        .then((result) => {
          this.$refs.documentViewer.onView(result);
        })
        .catch(() => {
          this.customError = 'Could not view file, please try again.';
        });

    },
    deleteFile() {
      SchemeBusinessService.deleteFile(this.value.fileUrl)
        .then(() => {
          this.value = { fileUrl: '', fileName: '' };
        });
    },
    clearError() {
      this.customError = '';
    },
  },
};
</script>
